function parseTags(value) {
  if (!value) return [];

  return String(value)
    .split(/\r?\n|,\s*/)
    .map((tag) => tag.trim())
    .filter(Boolean);
}

const elementName = 'aceii-hardware-specs-tabs';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiHardwareSpecsTabs extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.tabButtons = [...this.querySelectorAll('[data-hardware-tab]')];
      this.cardEls = [...this.querySelectorAll('[data-hardware-card]')];
      this.descriptionEl = this.querySelector('[data-hardware-description]');
      this.tabsScroller = this.querySelector('.aceii-hardware-specs__tabs');
      this.navShell = this.querySelector('[data-hardware-nav]');
      this.navSentinel = this.querySelector('.aceii-hardware-specs__nav-sentinel');
      this.cardGrid = this.querySelector('.aceii-hardware-specs__card-grid');
      this.cursorHint = this.querySelector('[data-hardware-cursor]');
      this.sectionVisible = false;
      this.documentVisible = !document.hidden;
      this.autoRotateMs = Number(this.dataset.autoRotateMs) || 15000;
      this.activeKey = this.tabButtons[0]?.dataset.hardwareTab ?? '';
      this.activeVideos = [];
      this.autoTimer = null;

      try {
        this.tabsData = JSON.parse(
          this.querySelector('[data-hardware-tabs-json]')?.textContent ?? '[]',
        );
      } catch {
        this.tabsData = [];
      }

      this.tabButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.activate(button.dataset.hardwareTab, { userInitiated: true }),
          { signal },
        );
      });

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          this.syncVideoPlayback();
        },
        { signal },
      );

      this.sectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = Boolean(entry?.isIntersecting);
          this.syncVideoPlayback();
        },
        {
          rootMargin: '160px 0px',
          threshold: 0.01,
        },
      );
      this.sectionObserver.observe(this);

      this.updateStickyState = this.updateStickyState.bind(this);
      window.addEventListener('scroll', this.updateStickyState, { passive: true, signal });
      window.addEventListener('resize', this.updateStickyState, { passive: true, signal });
      this.updateStickyState();

      if (this.cardGrid && this.cursorHint) {
        this.cardGrid.addEventListener(
          'pointermove',
          (event) => this.updateCursorHint(event),
          { signal },
        );
        this.cardGrid.addEventListener(
          'pointerleave',
          () => this.hideCursorHint(),
          { signal },
        );
      }

      if (this.cardGrid) {
        this.cardGrid.addEventListener(
          'click',
          (event) => this.handleCardGridClick(event),
          { signal },
        );
      }

      this.activate(this.activeKey, { userInitiated: false });
    }

    disconnectedCallback() {
      this.clearAutoTimer();
      this.pauseVideos();
      this.sectionObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }

    getTabRecord(key) {
      return this.tabsData.find((tab) => tab.key === key) ?? this.tabsData[0];
    }

    activate(key, { userInitiated }) {
      const record = this.getTabRecord(key);
      if (!record) return;

      this.activeKey = record.key;
      this.descriptionEl.textContent = record.headerDescription ?? '';

      this.tabButtons.forEach((button) => {
        const active = button.dataset.hardwareTab === record.key;
        button.classList.toggle('is-active', active);
        button.classList.toggle('is-progressing', false);
        button.setAttribute('aria-selected', String(active));
      });

      this.cardEls.forEach((card) => {
        card.classList.remove('is-changing');
      });
      void this.offsetWidth;

      this.activeVideos = [];
      this.cardEls.forEach((card, index) => {
        const cardData = record.cards?.[index] ?? {};
        this.renderCard(card, cardData);
        card.classList.add('is-changing');
      });

      const activeButton = this.tabButtons.find((button) => button.dataset.hardwareTab === record.key);
      if (activeButton) {
        activeButton.classList.add('is-progressing');
        this.centerActiveTab(activeButton);
      }

      if (userInitiated) {
        this.restartAutoRotate();
      } else {
        this.scheduleNext();
      }

      this.syncVideoPlayback();
    }

    renderCard(card, cardData) {
      const imageEl = card.querySelector('[data-card-image]');
      const videoEl = card.querySelector('[data-card-video]');
      const metaEl = card.querySelector('[data-card-meta]');
      const titleEl = card.querySelector('[data-card-title]');
      const textEl = card.querySelector('[data-card-text]');
      const tagsEl = card.querySelector('[data-card-tags]');

      const hasVideo = Boolean(cardData.video);
      card.classList.toggle('is-video', hasVideo);

      metaEl.textContent = cardData.meta ?? '';
      titleEl.textContent = cardData.title ?? '';
      textEl.textContent = cardData.text ?? '';

      imageEl.style.removeProperty('--ahs-card-media');
      if (cardData.image) {
        imageEl.style.setProperty('--ahs-card-media', `url('${cardData.image}')`);
      }

      videoEl.pause();
      videoEl.removeAttribute('src');
      videoEl.innerHTML = '';
      videoEl.dataset.src = '';
      videoEl.load();

      if (hasVideo) {
        videoEl.dataset.src = cardData.video;
        this.activeVideos.push(videoEl);
      }

      const tags = parseTags(cardData.tags);
      tagsEl.innerHTML = tags.map((tag) => `<span>${this.escapeHtml(tag)}</span>`).join('');
    }

    syncVideoPlayback() {
      const shouldPlay = this.sectionVisible && this.documentVisible;

      this.activeVideos.forEach((video) => {
        if (!video.dataset.src) return;

        if (!shouldPlay) {
          if (!video.paused) video.pause();
          return;
        }

        if (!video.currentSrc && !video.src) {
          video.src = video.dataset.src;
          video.load();
        }

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            video.pause();
          });
        }
      });
    }

    pauseVideos() {
      this.querySelectorAll('[data-card-video]').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    restartAutoRotate() {
      this.clearAutoTimer();
      this.scheduleNext();
    }

    scheduleNext() {
      this.clearAutoTimer();
      if (this.tabButtons.length < 2) return;

      this.autoTimer = window.setTimeout(() => {
        const currentIndex = this.tabButtons.findIndex(
          (button) => button.dataset.hardwareTab === this.activeKey,
        );
        const nextIndex = currentIndex >= 0
          ? (currentIndex + 1) % this.tabButtons.length
          : 0;
        this.activate(this.tabButtons[nextIndex].dataset.hardwareTab, { userInitiated: false });
      }, this.autoRotateMs);
    }

    clearAutoTimer() {
      if (this.autoTimer) {
        window.clearTimeout(this.autoTimer);
        this.autoTimer = null;
      }
    }

    centerActiveTab(button) {
      if (!button || !this.tabsScroller || window.innerWidth > 820) return;

      const scrollerRect = this.tabsScroller.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const targetLeft = this.tabsScroller.scrollLeft
        + buttonRect.left
        - scrollerRect.left
        - ((scrollerRect.width - buttonRect.width) / 2);

      this.tabsScroller.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });
    }

    updateStickyState() {
      if (!this.navShell || !this.navSentinel) return;
      const rect = this.navSentinel.getBoundingClientRect();
      this.navShell.classList.toggle('is-stuck', rect.top <= 0);
    }

    updateCursorHint(event) {
      if (!this.cursorHint || window.innerWidth <= 820) return;

      const gridRect = this.cardGrid.getBoundingClientRect();
      const isRight = event.clientX > (gridRect.left + (gridRect.width / 2));
      this.cursorHint.style.left = `${event.clientX}px`;
      this.cursorHint.style.top = `${event.clientY}px`;
      this.cursorHint.classList.add('is-visible');
      this.cursorHint.classList.toggle('is-right', isRight);
    }

    handleCardGridClick(event) {
      if (!this.cardGrid || this.tabButtons.length < 2) return;

      const gridRect = this.cardGrid.getBoundingClientRect();
      const currentIndex = this.tabButtons.findIndex(
        (button) => button.dataset.hardwareTab === this.activeKey,
      );

      if (currentIndex < 0) return;

      const moveForward = event.clientX > (gridRect.left + (gridRect.width / 2));
      const nextIndex = moveForward
        ? (currentIndex + 1) % this.tabButtons.length
        : (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length;

      this.activate(this.tabButtons[nextIndex].dataset.hardwareTab, { userInitiated: true });
    }

    hideCursorHint() {
      this.cursorHint?.classList.remove('is-visible', 'is-right');
    }

    escapeHtml(value) {
      return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }
  }

  window.customElements.define(elementName, AceiiHardwareSpecsTabs);
}
